/* eslint-disable @typescript-eslint/no-explicit-any -- any используется как значение параметров инвариантных типов */

import { DistributiveOmit, objectMap } from '@direct-frontend/stdlib';

import { Reaction } from '@adv-frontend/reactions';

import { type UnknownFormInstance } from '../form/types';
import { BaseValidationError } from '../types';

import {
    $internals,
    BaseFieldSelectorsMap,
    BaseFields,
    BoundFieldActions,
    BoundFieldAsyncActions,
    BoundFieldSelectors,
    FieldActionsMap,
    FieldAsyncActionsMap,
    FieldInstance,
    FieldSelectorsMap,
    IfFieldsRequireContextInput,
    InferFieldsContextInput,
    InferFieldsValue,
    SetValueOptions,
    SkipUnknownActions,
    SkipUnknownAsyncActions,
    SkipUnknownOrAny,
    SkipUnknownSelectors,
    SkipUnknownValidationError,
    ValidateFields,
} from './types';
import { FieldValidator } from './validation';
import { createReusableReactionApi } from './instanceHelpers';
import type { CreateReusableFieldReactionApi } from './instanceHelpers';
import { getDefaultValue, DefaultValueOrFactory } from '../utils/getDefaultValue';
import { FieldState, FieldStateGetters, ObjectFieldState } from '../store/types';

/** Базовый тип для любого "объектного" поля */
export type BaseObjectField = ObjectField<any, any, never, any, any, any, any>;
/** Базовый тип для инстанса любого "объектного" поля */
export type BaseObjectFieldInstance = ObjectFieldInstance<any, any, any, any, any, any, any>;

/**
 * Объектное поле – объединяет в себе одно или несколько полей произвольного типа
 * Важно использовать `interface`, чтобы иметь возможность описать циклический тип
 * @see https://github.com/microsoft/TypeScript/pull/33050
 **/
export interface ObjectField<
    InnerFields extends BaseFields,
    FieldValidationError extends BaseValidationError = never,
    FieldContextInput = never,
    FieldContext = never,
    FieldActions extends FieldActionsMap<
        InferFieldsValue<InnerFields>,
        ObjectFieldState<InferFieldsValue<InnerFields>, FieldValidationError>,
        FieldContext
    > = never,
    FieldSelectors extends FieldSelectorsMap<InferFieldsValue<InnerFields>> = never,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ObjectFieldInstanceWithoutAsyncActions<
            InnerFields,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors
        >
    > = never,
> {
    type: 'object';
    fields: InnerFields;
    defaultValue: InferFieldsValue<InnerFields>;
    validators?: ObjectFieldValidators<
        InnerFields,
        FieldValidationError,
        FieldContextInput,
        FieldContext,
        FieldSelectors
    >;
    reactions?: ObjectFieldReaction<never, never, never, never, never, never>[];
    actions?: FieldActions;
    selectors?: FieldSelectors;
    asyncActions?: FieldAsyncActions;
    context?: IfFieldsRequireContextInput<
        InnerFields,
        (input: FieldContextInput) => { object: FieldContext; fields: InferFieldsContextInput<InnerFields> },
        (input: FieldContextInput) => { object: FieldContext }
    >;
}

/**
 * Подменяет never на пустой объект, иначе возвращает исходный тип
 */
export type NeverToEmpty<T> = [T] extends [never] ? Record<string, unknown> : T;

/**
 * Инстанс "объектного" поля без asyncActions (используется для разрыва циклической зависимости)
 * @internal
 **/
export type ObjectFieldInstanceWithoutAsyncActions<
    InnerFields extends BaseFields,
    FieldValidationError extends BaseValidationError = never,
    FieldContext = never,
    FieldActions extends FieldActionsMap<
        InferFieldsValue<InnerFields>,
        ObjectFieldState<InferFieldsValue<InnerFields>, FieldValidationError>,
        FieldContext
    > = never,
    FieldSelectors extends FieldSelectorsMap<InferFieldsValue<InnerFields>> = never,
    FieldName extends string = string,
> = {
    /** Id инстанса поля */
    id: string;
    /** Тип поля */
    type: 'object';
    /** Название поля (может использоваться как дискриминант в юнионах) */
    name: FieldName;
    /** Полный путь до поля в форме */
    path: string[];
    /** Родительская форма поля */
    form: UnknownFormInstance;
    /** Инстансы всех вложенных полей */
    fields: {
        [FieldName in keyof InnerFields & string]: FieldInstance<InnerFields[FieldName], FieldName>;
    };
    /**
     * Обновляет текущее значение поля
     * @param value Новое значение поля
     * @param options Параметры для внутреннего использования
     **/
    setValue: (value: InferFieldsValue<NoInfer<InnerFields>>, options?: SetValueOptions) => void;
    /** Получает текущее значение поля */
    getValue: () => InferFieldsValue<NoInfer<InnerFields>>;
    /** Возвращает текущее состояние поля */
    getState: () => ObjectFieldState<InferFieldsValue<InnerFields>, FieldValidationError>;
    /**
     * Геттеры отдельных частей состояния поля
     *
     * Используйте `state` в реакциях и селекторах, а `getState()` — когда нужно всё состояние целиком
     *
     * @example
     * ```typescript
     * const isActionsRunning = form.getInstance('form-id').fields.myObject.state.isActionsRunning();
     * //    ^? boolean
     * ```
     */
    state: FieldStateGetters<InferFieldsValue<InnerFields>, FieldValidationError>;
    /** Возвращает текущее значение контекста поля */
    getContext: () => FieldContext;
    /** Запускает повторную валидацию поля и всех его дочерних полей */
    revalidate: () => void;
    /** Добавляет ошибку к форме */
    addValidationError: (error: DistributiveOmit<FieldValidationError, 'meta'>) => void;
    /** Добавляет ошибки к форме */
    addValidationErrors: (errors: DistributiveOmit<FieldValidationError, 'meta'>[]) => void;
    /** Экшены поля */
    actions: NeverToEmpty<BoundFieldActions<FieldActions>>;
    /** Селекторы поля */
    selectors: BoundFieldSelectors<FieldSelectors>;
    /** Внутренние методы, которые не доступны в публичном API */
    [$internals]: {
        /** Декларация поля */
        declaration: BaseObjectField;
        /** Получает текущее значение поля */
        useValue: () => InferFieldsValue<NoInfer<InnerFields>>;
        /** Возвращает текущее состояние поля */
        useState: () => FieldState<InferFieldsValue<InnerFields>, FieldValidationError>;
        /** Возвращает текущее значение контекста поля */
        useContext: () => FieldContext;
        /** Выставляет состояние isTouched у поля */
        touch: () => void;
        /** Скроллит страницу к полю */
        scrollToField: () => void;
    };
};

/**
 * Инстанс "объектного" поля – объединяет в себе одно или несколько полей произвольного типа
 * @typeParam InnerFields Дочерние поля объекта
 * @typeParam FieldValidationError Допустимые ошибки валидации объекта
 * @typeParam FieldContext Контекст поля
 * @typeParam FieldActions Экшены поля
 * @typeParam FieldSelectors Селекторы
 * @typeParam FieldName Название поля внутри родительского поля или формы
 **/
export type ObjectFieldInstance<
    InnerFields extends BaseFields,
    FieldValidationError extends BaseValidationError = never,
    FieldContext = never,
    FieldActions extends FieldActionsMap<
        InferFieldsValue<InnerFields>,
        ObjectFieldState<InferFieldsValue<InnerFields>, FieldValidationError>,
        FieldContext
    > = never,
    FieldSelectors extends FieldSelectorsMap<InferFieldsValue<InnerFields>> = never,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ObjectFieldInstanceWithoutAsyncActions<
            InnerFields,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors
        >
    > = never,
    FieldName extends string = string,
> = ObjectFieldInstanceWithoutAsyncActions<
    InnerFields,
    FieldValidationError,
    FieldContext,
    FieldActions,
    FieldSelectors,
    FieldName
> & {
    /** Асинхронные экшены поля */
    asyncActions: BoundFieldAsyncActions<FieldAsyncActions>;
};

/** Объект со всеми валидаторами "объектного" поля */
export type ObjectFieldValidators<
    InnerFields extends BaseFields,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldSelectors extends BaseFieldSelectorsMap,
> = {
    /** Валидаторы, которые будут запущены при изменении любого из полей объекта */
    onChange?: FieldValidator<
        ObjectField<InnerFields, FieldValidationError, FieldContextInput, FieldContext, never, FieldSelectors, never>
    >[];
    /** Валидаторы, которые будут запущены при отправке формы */
    onSubmit?: FieldValidator<
        ObjectField<InnerFields, FieldValidationError, FieldContextInput, FieldContext, never, FieldSelectors, never>
    >[];
};

/** Реакция "объектного" поля */
export type ObjectFieldReaction<
    InnerFields extends BaseFields,
    FieldValidationError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<
        InferFieldsValue<InnerFields>,
        ObjectFieldState<InferFieldsValue<InnerFields>, FieldValidationError>,
        FieldContext
    >,
    FieldSelectors extends FieldSelectorsMap<InferFieldsValue<InnerFields>>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ObjectFieldInstanceWithoutAsyncActions<
            InnerFields,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors
        >
    >,
> = Reaction<
    any,
    ObjectFieldInstance<
        InnerFields,
        NoInfer<FieldValidationError>,
        FieldContext,
        FieldActions,
        FieldSelectors,
        FieldAsyncActions
    >
>;

/** Параметры для создания "объектного" поля */
type ObjectFieldOptions<
    InnerFields extends BaseFields,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldActions extends FieldActionsMap<
        InferFieldsValue<InnerFields>,
        ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>
    >,
    FieldSelectors extends FieldSelectorsMap<InferFieldsValue<InnerFields>>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ObjectFieldInstanceWithoutAsyncActions<
            NoInfer<InnerFields>,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            SkipUnknownActions<
                InferFieldsValue<InnerFields>,
                ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
                SkipUnknownOrAny<FieldContext>,
                NoInfer<FieldActions>
            >,
            SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>
        >
    >,
> = {
    /** Вложенные поля */
    fields: ValidateFields<InnerFields> | (() => ValidateFields<InnerFields>);
    /**
     * Значение поля по умолчанию (будет использовано если начальное значение формы не задано)
     * Если не указано, то значение по умолчанию вычисляется отдельно по каждому вложенному полю
     **/
    defaultValue?: DefaultValueOrFactory<InferFieldsValue<InnerFields>>;
    /** Список валидаторов поля */
    validators?: (
        // Используем интерфейс с функцией, чтобы иметь возможность указать значение type-параметра руками
        createValidators: <PassedFieldValidationError extends BaseValidationError>(
            validators?: ObjectFieldValidators<
                InnerFields,
                PassedFieldValidationError,
                FieldContextInput,
                FieldContext,
                FieldSelectors
            >
        ) => ObjectFieldValidators<
            InnerFields,
            PassedFieldValidationError,
            FieldContextInput,
            FieldContext,
            FieldSelectors
        >
    ) => ObjectFieldValidators<
        InnerFields,
        SkipUnknownValidationError<FieldValidationError>,
        FieldContextInput,
        FieldContext,
        FieldSelectors
    >;
    /** Экшены поля */
    actions?: FieldActions;
    selectors?: FieldSelectors;
    /** Асинхронные экшены поля */
    asyncActions?: (
        createAsyncActions: <
            T extends FieldAsyncActionsMap<
                ObjectFieldInstanceWithoutAsyncActions<
                    NoInfer<InnerFields>,
                    SkipUnknownValidationError<FieldValidationError>,
                    SkipUnknownOrAny<FieldContext>,
                    FieldActions,
                    FieldSelectors
                >
            >,
        >(
            asyncActions: T
        ) => T
    ) => FieldAsyncActions;

    /** Реакции поля */
    reactions?: (
        createReactions: (
            reactions: ObjectFieldReaction<
                NoInfer<InnerFields>,
                SkipUnknownValidationError<FieldValidationError>,
                SkipUnknownOrAny<FieldContext>,
                SkipUnknownActions<
                    InferFieldsValue<InnerFields>,
                    ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
                    SkipUnknownOrAny<FieldContext>,
                    NoInfer<FieldActions>
                >,
                SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>,
                SkipUnknownAsyncActions<FieldAsyncActions>
            >[]
        ) => ObjectFieldReaction<
            NoInfer<InnerFields>,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            SkipUnknownActions<
                InferFieldsValue<InnerFields>,
                ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
                SkipUnknownOrAny<FieldContext>,
                NoInfer<FieldActions>
            >,
            SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>,
            SkipUnknownAsyncActions<FieldAsyncActions>
        >[],
        createReusableReaction: CreateReusableFieldReactionApi<
            ObjectField<
                NoInfer<InnerFields>,
                SkipUnknownValidationError<FieldValidationError>,
                FieldContextInput,
                SkipUnknownOrAny<FieldContext>,
                SkipUnknownActions<
                    InferFieldsValue<InnerFields>,
                    ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
                    SkipUnknownOrAny<FieldContext>,
                    NoInfer<FieldActions>
                >,
                SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>,
                SkipUnknownAsyncActions<NoInfer<FieldAsyncActions>>
            >,
            ObjectFieldReaction<
                NoInfer<InnerFields>,
                SkipUnknownValidationError<FieldValidationError>,
                SkipUnknownOrAny<FieldContext>,
                SkipUnknownActions<
                    InferFieldsValue<InnerFields>,
                    ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
                    SkipUnknownOrAny<FieldContext>,
                    NoInfer<FieldActions>
                >,
                SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>,
                SkipUnknownAsyncActions<FieldAsyncActions>
            >
        >
    ) => ObjectFieldReaction<
        NoInfer<InnerFields>,
        SkipUnknownValidationError<FieldValidationError>,
        SkipUnknownOrAny<FieldContext>,
        SkipUnknownActions<
            InferFieldsValue<InnerFields>,
            ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
            SkipUnknownOrAny<FieldContext>,
            NoInfer<FieldActions>
        >,
        SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>,
        SkipUnknownAsyncActions<FieldAsyncActions>
    >[];
} & IfFieldsRequireContextInput<
    InnerFields,
    {
        /** Функция, наполняющая контекст всего объекта и его дочерних полей */
        context: (input: FieldContextInput) => { object?: FieldContext; fields: InferFieldsContextInput<InnerFields> };
    },
    {
        /** Функция, наполняющая контекст всего объекта */
        context?: (input: FieldContextInput) => { object?: FieldContext };
    }
>;

/** Создает "объектное" поле */
export function createObjectField<
    InnerFields extends BaseFields,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldActions extends FieldActionsMap<
        InferFieldsValue<InnerFields>,
        ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>
    >,
    FieldSelectors extends FieldSelectorsMap<InferFieldsValue<InnerFields>>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ObjectFieldInstanceWithoutAsyncActions<
            NoInfer<InnerFields>,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            SkipUnknownActions<
                InferFieldsValue<InnerFields>,
                ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
                SkipUnknownOrAny<FieldContext>,
                NoInfer<FieldActions>
            >,
            SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>
        >
    >,
>({
    actions,
    asyncActions,
    selectors,
    context,
    defaultValue,
    fields: validatedFields,
    validators,
    reactions,
}: ObjectFieldOptions<
    InnerFields,
    FieldValidationError,
    SkipUnknownOrAny<FieldContextInput>,
    SkipUnknownOrAny<FieldContext>,
    FieldActions,
    FieldSelectors,
    FieldAsyncActions
>): ObjectField<
    NoInfer<InnerFields>,
    SkipUnknownValidationError<FieldValidationError>,
    SkipUnknownOrAny<FieldContextInput>,
    SkipUnknownOrAny<FieldContext>,
    SkipUnknownActions<
        InferFieldsValue<InnerFields>,
        ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>,
        NoInfer<FieldActions>
    >,
    SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>,
    SkipUnknownAsyncActions<NoInfer<FieldAsyncActions>>
> {
    // Приводим тип к корректному, так как в параметрах функции на уровне типа сделана валидация
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const fields = validatedFields as InnerFields | (() => InnerFields);
    const resolvedDefaultValue = getDefaultValue(defaultValue);

    // Приводим тип к корректному, так как в возвращаемом значении параметры уточняются (удаляются any и unknown)
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return {
        type: 'object',
        // Делаем поля геттером чтобы была возможность описывать поля с рекурсивной вложенностью
        // И не было ошибки Cannot access variable before initialization
        get fields() {
            return typeof fields === 'function' ? fields() : fields;
        },
        context,
        actions,
        asyncActions: asyncActions?.(asyncActionsArg => asyncActionsArg),
        selectors,
        defaultValue: resolvedDefaultValue ?? objectMap(fields, field => getDefaultValue(field?.defaultValue)),
        reactions: reactions?.(reaction => reaction, createReusableReactionApi()),
        validators: validators?.(value => value ?? {}),
    } as ObjectField<
        NoInfer<InnerFields>,
        SkipUnknownValidationError<FieldValidationError>,
        SkipUnknownOrAny<FieldContextInput>,
        SkipUnknownOrAny<FieldContext>,
        SkipUnknownActions<
            InferFieldsValue<InnerFields>,
            ObjectFieldState<InferFieldsValue<InnerFields>, SkipUnknownValidationError<FieldValidationError>>,
            SkipUnknownOrAny<FieldContext>,
            NoInfer<FieldActions>
        >,
        SkipUnknownSelectors<InferFieldsValue<InnerFields>, NoInfer<FieldSelectors>>,
        SkipUnknownAsyncActions<NoInfer<FieldAsyncActions>>
    >;
}
